import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Hashids from 'hashids';
import React, { useEffect, useMemo, useState } from 'react';
import { Trash2 as TrashIcon } from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  type AdminRole,
  deleteRole,
  getApiErrorMessage,
  getRole,
  hasPermission,
  updateRole,
} from '@/api/admin-access';
import Breadcrumb from '@/components/common/breadcrumb.component';
import { UserStore } from '@/stores';

const hashids = new Hashids('', 12);

const RoleDetailsView = () => {
  const navigate = useNavigate();
  const access = UserStore.useState((state) => state.access);
  const canWrite = hasPermission(access, 'settings', 'write');
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [loadError, setLoadError] = useState('');

  const roleId = useMemo(() => {
    const decoded = hashids.decode(id || '');
    const value = decoded[0];
    return typeof value === 'number' ? value : null;
  }, [id]);

  useEffect(() => {
    if (!roleId) {
      setLoadError('Invalid role ID');
      setLoading(false);
      return;
    }
    getRole(roleId)
      .then(setRole)
      .catch((error) => setLoadError(getApiErrorMessage(error)))
      .finally(() => setLoading(false));
  }, [roleId]);

  const permissionSections = useMemo(() => {
    const sections = new Map<string, AdminRole['permissions']>();
    (role?.permissions || []).forEach((permission) => {
      const section = permission.menuSection || 'General';
      sections.set(section, [...(sections.get(section) || []), permission]);
    });
    return [...sections.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [role]);

  const updateField = (
    field: 'name' | 'key' | 'description',
    value: string
  ) => {
    setRole((current) => (current ? { ...current, [field]: value } : current));
  };

  const handleToggle = (
    permissionGroupId: number,
    field: 'can_read' | 'can_write',
    checked: boolean
  ) => {
    setRole((current) => {
      if (!current) return current;
      return {
        ...current,
        permissions: current.permissions.map((permission) => {
          if (permission.permission_group_id !== permissionGroupId) {
            return permission;
          }
          if (field === 'can_write') {
            return {
              ...permission,
              can_write: checked,
              can_read: checked || permission.can_read,
            };
          }
          return {
            ...permission,
            can_read: checked,
            can_write: checked ? permission.can_write : false,
          };
        }),
      };
    });
  };

  const saveRole = async () => {
    if (!roleId || !role) return;
    if (!role.name.trim() || !role.key.trim()) {
      toast.error('Role name and key are required');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateRole(roleId, {
        name: role.name.trim(),
        key: role.key.trim(),
        description: role.description?.trim() || null,
        permissions: role.permissions,
      });
      setRole(updated);
      toast.success('Role saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!roleId) return;
    setDeleting(true);
    try {
      await deleteRole(roleId);
      toast.success('Role deleted');
      navigate('/settings/roles');
    } catch (error) {
      toast.error(getApiErrorMessage(error));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Breadcrumb title="Role Details" label="Role" parent="Roles" />
        <Box py={8} textAlign="center">
          <CircularProgress size={28} />
        </Box>
      </div>
    );
  }

  if (!role || loadError) {
    return (
      <div>
        <Breadcrumb title="Role Details" label="Role" parent="Roles" />
        <div className="container-fluid">
          <Alert severity="error" sx={{ maxWidth: 800, margin: '30px auto' }}>
            {loadError || 'Role not found'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb title={role.name} label="Role" parent="Roles" />

      <div className="container-fluid">
        <Box sx={{ maxWidth: 900, width: '100%', margin: '30px auto' }}>
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              mb={3}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6">Role details</Typography>
                  {role.is_system && <Chip size="small" label="System" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Permissions are resolved by the API each time access is
                  checked.
                </Typography>
              </Box>
              {!role.is_system && canWrite && (
                <Button
                  color="error"
                  startIcon={<TrashIcon size={17} />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              )}
            </Stack>

            <Stack spacing={2} mb={3}>
              <TextField
                required
                label="Name"
                value={role.name}
                disabled={!canWrite}
                onChange={(event) => updateField('name', event.target.value)}
              />
              <TextField
                required
                label="Key"
                value={role.key}
                disabled={!canWrite || role.is_system}
                helperText={
                  role.is_system
                    ? 'System role keys cannot be changed.'
                    : 'Stable identifier for this role.'
                }
                onChange={(event) => updateField('key', event.target.value)}
              />
              <TextField
                label="Description"
                value={role.description || ''}
                disabled={!canWrite}
                multiline
                minRows={2}
                onChange={(event) =>
                  updateField('description', event.target.value)
                }
              />
            </Stack>

            <Divider />
            <Box mt={3}>
              <Typography variant="h6">Permissions</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Write access automatically includes read access.
              </Typography>

              {permissionSections.length === 0 ? (
                <Alert severity="info">
                  No permission groups are configured in the database.
                </Alert>
              ) : (
                permissionSections.map(([section, permissions]) => (
                  <Box key={section} mb={3}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ px: 2 }}
                    >
                      {section}
                    </Typography>
                    <List disablePadding>
                      {permissions
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((permission) => (
                          <ListItem
                            key={permission.permission_group_id}
                            divider
                            sx={{
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              flexDirection: { xs: 'column', sm: 'row' },
                            }}
                            secondaryAction={
                              <Stack direction="row" spacing={1}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={permission.can_read}
                                      disabled={!canWrite}
                                      onChange={(event) =>
                                        handleToggle(
                                          permission.permission_group_id,
                                          'can_read',
                                          event.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label="Read"
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={permission.can_write}
                                      disabled={!canWrite}
                                      onChange={(event) =>
                                        handleToggle(
                                          permission.permission_group_id,
                                          'can_write',
                                          event.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label="Write"
                                />
                              </Stack>
                            }
                          >
                            <ListItemText
                              primary={permission.name}
                              secondary={
                                permission.description || permission.key
                              }
                              sx={{ pr: { sm: 24 } }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Box>
                ))
              )}
            </Box>

            {canWrite && (
              <Stack direction="row" justifyContent="flex-end" mt={3}>
                <Button
                  disabled={saving}
                  variant="contained"
                  onClick={saveRole}
                >
                  {saving ? 'Saving…' : 'Save role'}
                </Button>
              </Stack>
            )}
          </Card>
        </Box>
      </div>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {role.name}?</DialogTitle>
        <DialogContent>
          This role can only be deleted when it is no longer assigned to any
          admin users.
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            disabled={deleting}
            color="error"
            variant="contained"
            onClick={handleDelete}
          >
            {deleting ? 'Deleting…' : 'Delete role'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RoleDetailsView;
